import { useState, useEffect, useCallback } from 'react';
import '../styles/responsive/pages/Developer.css';
import {
  Key,
  Copy,
  Check,
  Plus,
  Trash2,
  Code,
  Book,
  Terminal,
  Shield,
  X,
  ExternalLink,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Globe,
  Zap,
  Lock,
  FileText,
  Edit3,
  Eye,
  EyeOff,
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { fetchWithAuth } from '../utils/api';
import RequireFeature from '../components/common/RequireFeature';
import UpsellModal from '../components/common/UpsellModal';
import Skeleton from '../components/common/Skeleton';
import toast from 'react-hot-toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Developer = () => {
  const { mode, canGoLive } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Onboarding deep-link: show a "Back to setup" banner so the merchant can copy
  // their test key/snippet here and return to finish onboarding.
  const fromOnboarding = searchParams.get('onboarding') === '1';

  const [activeTab, setActiveTab] = useState('api-keys');
  // G-1: feature being upsold when a Free user taps a locked tab's CTA.
  const [upsellFeature, setUpsellFeature] = useState(null);
  const [apiKeys, setApiKeys] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0, revoked: 0 });

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [editingKey, setEditingKey] = useState(null);

  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [revokingKey, setRevokingKey] = useState(null);
  const [isRevoking, setIsRevoking] = useState(false);

  // API secrets (X-API-SECRET). Revealed values live only in memory, per key.
  const [revealedSecrets, setRevealedSecrets] = useState({});
  const [secretLoadingId, setSecretLoadingId] = useState(null);
  const [rotatingKey, setRotatingKey] = useState(null);
  const [isRotating, setIsRotating] = useState(false);
  // { name, apiKey?, secretKey, rotated } -- the "save your secret" dialog.
  const [secretNotice, setSecretNotice] = useState(null);

  // API-03: webhook delivery log.
  const [deliveries, setDeliveries] = useState([]);
  const [deliveriesLoading, setDeliveriesLoading] = useState(false);
  const [expandedDelivery, setExpandedDelivery] = useState(null);
  const [resendingId, setResendingId] = useState(null);

  const fetchDeliveries = useCallback(async () => {
    setDeliveriesLoading(true);
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/webhooks/deliveries?limit=50`);
      const data = await response.json();
      if (data.success) setDeliveries(data.data.deliveries || []);
    } catch (error) {
      // Delivery log is supplementary; ignore fetch failures.
    } finally {
      setDeliveriesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'webhooks') fetchDeliveries();
  }, [activeTab, fetchDeliveries]);

  const handleResendDelivery = async (id) => {
    setResendingId(id);
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/webhooks/deliveries/${id}/resend`, { method: 'POST' });
      const data = await response.json();
      if (data.success) {
        toast.success('Webhook resent');
      } else {
        toast.error(data.message || 'Resend failed');
      }
      fetchDeliveries();
    } catch (error) {
      toast.error('Failed to resend webhook');
    } finally {
      setResendingId(null);
    }
  };

  const [newKeyForm, setNewKeyForm] = useState({
    name: '',
    description: '',
    webhookUrl: '',
  });
  // Environment for the key being created — an explicit choice, defaulted to the
  // current view mode. (Live is disabled until the account is activated.)
  const [keyEnv, setKeyEnv] = useState('test');
  useEffect(() => {
    if (showCreateModal) setKeyEnv(canGoLive ? mode : 'test');
  }, [showCreateModal, mode, canGoLive]);
  const [editWebhookUrl, setEditWebhookUrl] = useState('');

  const [copiedKey, setCopiedKey] = useState(null);

  const [expandedSection, setExpandedSection] = useState('quickstart');
  const [selectedLanguage, setSelectedLanguage] = useState('curl');

  const fetchApiKeys = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const isTestnet = mode === 'test';
      const endpoint = `${API_BASE_URL}/api-keys?isTestnet=${isTestnet}`;
      const response = await fetchWithAuth(endpoint);
      const data = await response.json();
      if (data.success) {
        setApiKeys(data.data.apiKeys);
      }
    } catch (error) {
      console.error('Failed to fetch API keys:', error);
      toast.error('Failed to load API keys');
    } finally {
      setIsLoading(false);
    }
  }, [mode]);

  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const isTestnet = mode === 'test';
      const endpoint = `${API_BASE_URL}/api-keys/stats?isTestnet=${isTestnet}`;
      const response = await fetchWithAuth(endpoint);
      const data = await response.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [mode]);

  useEffect(() => {
    fetchApiKeys();
    fetchStats();
  }, [fetchApiKeys, fetchStats]);

  const handleCopy = (key, value) => {
    navigator.clipboard.writeText(value);
    setCopiedKey(key);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const handleCreateApiKey = async (e) => {
    e.preventDefault();
    if (!newKeyForm.name.trim()) {
      toast.error('API key name is required');
      return;
    }

    if (!newKeyForm.webhookUrl.trim()) {
      toast.error('Webhook URL is required');
      return;
    }

    try {
      new URL(newKeyForm.webhookUrl);
    } catch {
      toast.error('Please enter a valid webhook URL');
      return;
    }

    setIsCreating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetchWithAuth(`${API_BASE_URL}/api-keys`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          name: newKeyForm.name,
          description: newKeyForm.description || null,
          webhookUrl: newKeyForm.webhookUrl,
          isTestnet: keyEnv === 'test',
        }),
      });

      const data = await response.json();
      if (data.success) {
        toast.success(`${keyEnv === 'test' ? 'Test' : 'Live'} API key created successfully!`);
        if (keyEnv !== mode) {
          toast(`Switch to ${keyEnv === 'test' ? 'Test' : 'Live'} mode to see this key.`, { icon: 'ℹ️' });
        }
        setShowCreateModal(false);
        if (data.data?.secretKey) {
          setSecretNotice({
            name: data.data.name,
            apiKey: data.data.apiKey,
            secretKey: data.data.secretKey,
            rotated: false,
          });
        }
        setNewKeyForm({
          name: '',
          description: '',
          webhookUrl: '',
        });
        fetchApiKeys();
        fetchStats();
      } else {
        toast.error(data.message || 'Failed to create API key');
      }
    } catch (error) {
      console.error('Failed to create API key:', error);
      toast.error('Failed to create API key');
    } finally {
      setIsCreating(false);
    }
  };

  const handleToggleStatus = async (keyId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetchWithAuth(`${API_BASE_URL}/api-keys/${keyId}/toggle`, {
        method: 'PATCH',
      });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message);
        fetchApiKeys();
        fetchStats();
      } else {
        toast.error(data.message || 'Failed to toggle status');
      }
    } catch (error) {
      console.error('Failed to toggle status:', error);
      toast.error('Failed to toggle status');
    }
  };

  const handleOpenRevokeModal = (key) => {
    setRevokingKey(key);
    setShowRevokeModal(true);
  };

  const handleCloseRevokeModal = () => {
    setShowRevokeModal(false);
    setRevokingKey(null);
  };

  const handleRevokeKey = async () => {
    if (!revokingKey) return;

    setIsRevoking(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetchWithAuth(`${API_BASE_URL}/api-keys/${revokingKey.id}`, {
        method: 'DELETE',
      });
      const data = await response.json();
      if (data.success) {
        toast.success('API key revoked successfully');
        handleCloseRevokeModal();
        fetchApiKeys();
        fetchStats();
      } else {
        toast.error(data.message || 'Failed to revoke API key');
      }
    } catch (error) {
      console.error('Failed to revoke API key:', error);
      toast.error('Failed to revoke API key');
    } finally {
      setIsRevoking(false);
    }
  };

  const handleToggleSecret = async (key) => {
    if (revealedSecrets[key.id]) {
      setRevealedSecrets((prev) => {
        const next = { ...prev };
        delete next[key.id];
        return next;
      });
      return;
    }
    setSecretLoadingId(key.id);
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api-keys/${key.id}/secret/reveal`, {
        method: 'POST',
      });
      const data = await response.json();
      if (data.success && data.data?.secretKey) {
        setRevealedSecrets((prev) => ({ ...prev, [key.id]: data.data.secretKey }));
      } else {
        toast.error(data.message || 'Failed to load the API secret');
      }
    } catch (error) {
      console.error('Failed to reveal API secret:', error);
      toast.error('Failed to load the API secret');
    } finally {
      setSecretLoadingId(null);
    }
  };

  const handleRotateSecret = async () => {
    if (!rotatingKey) return;
    setIsRotating(true);
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api-keys/${rotatingKey.id}/secret/rotate`, {
        method: 'POST',
      });
      const data = await response.json();
      if (data.success && data.data?.secretKey) {
        setRevealedSecrets((prev) => {
          const next = { ...prev };
          delete next[rotatingKey.id];
          return next;
        });
        setSecretNotice({ name: rotatingKey.name, secretKey: data.data.secretKey, rotated: true });
        setRotatingKey(null);
      } else {
        toast.error(data.message || 'Failed to rotate the API secret');
      }
    } catch (error) {
      console.error('Failed to rotate API secret:', error);
      toast.error('Failed to rotate the API secret');
    } finally {
      setIsRotating(false);
    }
  };

  const handleOpenEditModal = (key) => {
    setEditingKey(key);
    setEditWebhookUrl(key.webhookUrl || '');
    setShowEditModal(true);
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setNewKeyForm({
      name: '',
      description: '',
      webhookUrl: '',
    });
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingKey(null);
    setEditWebhookUrl('');
  };

  const handleUpdateWebhook = async (e) => {
    e.preventDefault();
    if (!editingKey) return;

    setIsUpdating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetchWithAuth(`${API_BASE_URL}/api-keys/${editingKey.id}`, {
        method: 'PUT',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          webhookUrl: editWebhookUrl || null,
        }),
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Webhook URL updated successfully!');
        setShowEditModal(false);
        setEditingKey(null);
        setEditWebhookUrl('');
        fetchApiKeys();
      } else {
        toast.error(data.message || 'Failed to update webhook URL');
      }
    } catch (error) {
      console.error('Failed to update webhook URL:', error);
      toast.error('Failed to update webhook URL');
    } finally {
      setIsUpdating(false);
    }
  };

  const getCodeExamples = (apiKey) => {
    const backendUrl = API_BASE_URL.replace('/api', '');
    const key = apiKey || 'pk_live_your_api_key_here';

    return {
      curl: `# Create a payment session
curl -X POST ${backendUrl}/api/v1/payments/sessions \\
  -H "X-API-KEY: ${key}" \\
  -H "X-API-SECRET: $MAXELPAY_API_SECRET" \\
  -H "Content-Type: application/json" \\
  -d '{
    "orderId": "order_123",
    "amount": 99.99,
    "currency": "USD",
    "description": "Order #123 - Premium Package",
    "successUrl": "https://yoursite.com/success",
    "cancelUrl": "https://yoursite.com/cancel",
    "callbackUrl": "https://yoursite.com/webhook"
  }'`,

      javascript: `// Using Node.js with fetch
const createPaymentSession = async (orderId, amount) => {
  const response = await fetch('${backendUrl}/api/v1/payments/sessions', {
    method: 'POST',
    headers: {
      'X-API-KEY': '${key}',
      'X-API-SECRET': process.env.MAXELPAY_API_SECRET, // sk_..., server-side only
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      orderId,
      amount,
      currency: 'USD',
      description: \`Order #\${orderId}\`,
      successUrl: 'https://yoursite.com/success',
      cancelUrl: 'https://yoursite.com/cancel',
      callbackUrl: 'https://yoursite.com/webhook',
    }),
  });

  const data = await response.json();

  if (data.success) {
    // Redirect customer to payment page
    window.location.href = data.data.paymentUrl;
  }

  return data;
};

// Usage
createPaymentSession('order_123', 99.99);`,

      python: `import os
import requests

def create_payment_session(order_id, amount):
    url = '${backendUrl}/api/v1/payments/sessions'
    headers = {
        'X-API-KEY': '${key}',
        'X-API-SECRET': os.environ['MAXELPAY_API_SECRET'],  # sk_..., server-side only
        'Content-Type': 'application/json'
    }
    payload = {
        'orderId': order_id,
        'amount': amount,
        'currency': 'USD',
        'description': f'Order #{order_id}',
        'successUrl': 'https://yoursite.com/success',
        'cancelUrl': 'https://yoursite.com/cancel',
        'callbackUrl': 'https://yoursite.com/webhook'
    }

    response = requests.post(url, json=payload, headers=headers)
    data = response.json()

    if data['success']:
        # Return payment URL for redirect
        return data['data']['paymentUrl']

    raise Exception(data['message'])

# Usage
payment_url = create_payment_session('order_123', 99.99)
print(f'Redirect customer to: {payment_url}')`,

      php: `<?php
function createPaymentSession($orderId, $amount) {
    $url = '${backendUrl}/api/v1/payments/sessions';

    $data = [
        'orderId' => $orderId,
        'amount' => $amount,
        'currency' => 'USD',
        'description' => "Order #{$orderId}",
        'successUrl' => 'https://yoursite.com/success',
        'cancelUrl' => 'https://yoursite.com/cancel',
        'callbackUrl' => 'https://yoursite.com/webhook'
    ];

    $options = [
        'http' => [
            'method' => 'POST',
            'header' => [
                'X-API-KEY: ${key}',
                'X-API-SECRET: ' . getenv('MAXELPAY_API_SECRET'),
                'Content-Type: application/json'
            ],
            'content' => json_encode($data)
        ]
    ];

    $context = stream_context_create($options);
    $result = file_get_contents($url, false, $context);
    $response = json_decode($result, true);

    if ($response['success']) {
        // Redirect to payment page
        header('Location: ' . $response['data']['paymentUrl']);
        exit;
    }

    throw new Exception($response['message']);
}

// Usage
createPaymentSession('order_123', 99.99);
?>`,
    };
  };

  const webhookExample = `// Webhook handler example (Express.js)
const crypto = require('crypto');

// Keep the raw body: the signature is computed over the exact bytes sent.
app.post('/webhook', express.json({ verify: (req, _res, buf) => { req.rawBody = buf; } }), async (req, res) => {
  const signature = req.get('x-webhook-signature') || '';
  const expected = crypto
    .createHmac('sha256', process.env.MAXELPAY_API_SECRET) // your sk_... secret
    .update(req.rawBody)
    .digest('hex');
  const valid = signature.length === expected.length &&
    crypto.timingSafeEqual(Buffer.from(signature), Buffer.from(expected));
  if (!valid) return res.status(401).json({ error: 'Invalid signature' });

  const { event, data } = req.body;

  switch (event) {
    case 'payment.completed':
    case 'payment.overpaid':
      // Paid in full: fulfill once per orderId (deliveries can repeat)
      console.log('Paid:', data.orderId, data.amount, data.currency, data.txHash);
      break;

    case 'payment.partial':
      // Part paid: record it, do not fulfill yet
      console.log('Partial:', data.orderId, data.totalPaidUsd, 'remaining', data.remainingAmountUsd);
      break;

    case 'payment.failed':
    case 'payment.expired':
      console.log('Not paid:', data.orderId, event);
      break;
  }

  res.json({ received: true });
});`;

  return (
    <div className="developer-page">
      {fromOnboarding && (
        <div className="onboarding-return-banner">
          <span>
            You are setting up MaxelPay. Copy your test key and snippet, then head
            back to finish setup.
          </span>
          <button
            type="button"
            className="onboarding-return-btn"
            onClick={() => navigate('/onboarding')}
          >
            Back to setup
          </button>
        </div>
      )}
      <div className="page-header">
        <div>
          <h1>Developers</h1>
          <p>API keys, webhooks, and integration tools.</p>
        </div>
        <div className="header-actions">
          <span className={`mode-badge ${mode === 'test' ? 'test' : 'live'}`}>
            {mode === 'test' ? 'Test mode' : 'Live mode'}
          </span>
        </div>
      </div>

      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-info">
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Total Keys</span>
          </div>
          <div className="stat-icon">
            <Key size={18} />
          </div>
        </div>
        <div className="stat-card active">
          <div className="stat-info">
            <span className="stat-value">{stats.active}</span>
            <span className="stat-label">Active</span>
          </div>
          <div className="stat-icon active">
            <CheckCircle size={18} />
          </div>
        </div>
        <div className="stat-card inactive">
          <div className="stat-info">
            <span className="stat-value">{stats.inactive}</span>
            <span className="stat-label">Inactive</span>
          </div>
          <div className="stat-icon inactive">
            <AlertCircle size={18} />
          </div>
        </div>
        <div className="stat-card revoked">
          <div className="stat-info">
            <span className="stat-value">{stats.revoked}</span>
            <span className="stat-label">Revoked</span>
          </div>
          <div className="stat-icon revoked">
            <Trash2 size={18} />
          </div>
        </div>
      </div>

      <div className="developer-tabs">
        <button
          className={`tab ${activeTab === 'api-keys' ? 'active' : ''}`}
          onClick={() => setActiveTab('api-keys')}
        >
          <Key size={18} />
          API Keys
        </button>
        <button
          className={`tab ${activeTab === 'documentation' ? 'active' : ''}`}
          onClick={() => setActiveTab('documentation')}
        >
          <Book size={18} />
          Documentation
        </button>
        <button
          className={`tab ${activeTab === 'webhooks' ? 'active' : ''}`}
          onClick={() => setActiveTab('webhooks')}
        >
          <Terminal size={18} />
          Webhooks
        </button>
      </div>

      {activeTab === 'api-keys' && (
        <RequireFeature
          feature="api_access"
          fallback={
            <div className="empty-state">
              <Lock size={48} />
              <h3>API Access is a Premium feature</h3>
              <p>Upgrade to create API keys and integrate MaxelPay into your application.</p>
              <button className="btn-primary" onClick={() => setUpsellFeature('api_access')}>
                <Lock size={18} />
                Upgrade to unlock
              </button>
            </div>
          }
        >
        <div className="api-keys-section">
          <div className="api-keys-header">
            <h3>Your API Keys</h3>
            <button className="create-key-btn" onClick={() => setShowCreateModal(true)}>
              <Plus size={18} />
              Create API Key
            </button>
          </div>

          {isLoading ? (
            <div className="api-keys-list">
              <Skeleton variant="card" />
              <Skeleton variant="card" />
              <Skeleton variant="card" />
            </div>
          ) : apiKeys.length === 0 ? (
            <div className="empty-state">
              <Key size={48} />
              <h3>No API keys yet</h3>
              <p>Create your first API key to start accepting crypto payments.</p>
              <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
                <Plus size={18} />
                Create your first API key
              </button>
              <div className="empty-hint">
                Not sure where to start?{' '}
                <button type="button" onClick={() => setActiveTab('documentation')}>
                  Read the docs →
                </button>
              </div>
            </div>
          ) : (
            <div className="api-keys-list">
              {apiKeys.map((key) => (
                <div key={key.id} className={`api-key-card ${key.status}`}>
                  <div className="key-header">
                    <div className="key-title">
                      <h4>{key.name}</h4>
                      <span className={`status-badge ${key.status}`}>{key.status}</span>
                    </div>
                    <div className="key-actions">
                      <button
                        className={`toggle-btn ${key.status === 'active' ? 'active' : ''}`}
                        onClick={() => handleToggleStatus(key.id)}
                        title={key.status === 'active' ? 'Deactivate' : 'Activate'}
                        disabled={key.status === 'revoked'}
                      >
                        {key.status === 'active' ? 'Active' : 'Inactive'}
                      </button>
                      <button
                        className="icon-btn"
                        onClick={() => handleOpenEditModal(key)}
                        title="Edit Webhook URL"
                        aria-label="Edit webhook URL"
                        disabled={key.status === 'revoked'}
                      >
                        <Edit3 size={16} />
                      </button>
                      <button
                        className="icon-btn danger"
                        onClick={() => handleOpenRevokeModal(key)}
                        title="Revoke Key"
                        aria-label="Revoke API key"
                        disabled={key.status === 'revoked'}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>

                  {key.description && (
                    <p className="key-description">{key.description}</p>
                  )}

                  <div className="key-details">
                    <div className="key-field">
                      <label>API Key</label>
                      <div className="key-value-row">
                        <code>{key.apiKey}</code>
                        <button
                          className="icon-btn"
                          onClick={() => handleCopy(`api-${key.id}`, key.apiKey)}
                          aria-label="Copy API key"
                        >
                          {copiedKey === `api-${key.id}` ? <Check size={14} /> : <Copy size={14} />}
                        </button>
                      </div>
                    </div>

                    <div className="key-field">
                      <label>API Secret</label>
                      <div className="key-value-row">
                        <code>{revealedSecrets[key.id] || 'sk_' + '•'.repeat(24)}</code>
                        <button
                          className="icon-btn"
                          onClick={() => handleToggleSecret(key)}
                          aria-label={revealedSecrets[key.id] ? 'Hide API secret' : 'Reveal API secret'}
                          title={revealedSecrets[key.id] ? 'Hide' : 'Reveal'}
                          disabled={key.status === 'revoked' || secretLoadingId === key.id}
                        >
                          {secretLoadingId === key.id ? (
                            <RefreshCw size={14} className="spinning" />
                          ) : revealedSecrets[key.id] ? (
                            <EyeOff size={14} />
                          ) : (
                            <Eye size={14} />
                          )}
                        </button>
                        {revealedSecrets[key.id] && (
                          <button
                            className="icon-btn"
                            onClick={() => handleCopy(`secret-${key.id}`, revealedSecrets[key.id])}
                            aria-label="Copy API secret"
                          >
                            {copiedKey === `secret-${key.id}` ? <Check size={14} /> : <Copy size={14} />}
                          </button>
                        )}
                        <button
                          className="icon-btn"
                          onClick={() => setRotatingKey(key)}
                          aria-label="Rotate API secret"
                          title="Rotate secret"
                          disabled={key.status === 'revoked'}
                        >
                          <RefreshCw size={14} />
                        </button>
                      </div>
                    </div>

                    <div className="key-meta">
                      <span>
                        <strong>Created:</strong>{' '}
                        {new Date(key.createdAt).toLocaleDateString()}
                      </span>
                      {key.lastUsedAt && (
                        <span>
                          <strong>Last Used:</strong>{' '}
                          {new Date(key.lastUsedAt).toLocaleDateString()}
                        </span>
                      )}
                      {key.webhookUrl && (
                        <span>
                          <strong>Webhook:</strong>{' '}
                          {key.webhookUrl}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        </RequireFeature>
      )}

      {activeTab === 'documentation' && (
        <div className="documentation-section">
          <div className="doc-card">
            <div
              className="doc-card-header"
              onClick={() => setExpandedSection(expandedSection === 'quickstart' ? null : 'quickstart')}
            >
              <div className="doc-title">
                <Zap size={20} />
                <h3>Quick Start Guide</h3>
              </div>
              {expandedSection === 'quickstart' ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            </div>
            {expandedSection === 'quickstart' && (
              <div className="doc-card-body">
                <div className="steps">
                  <div className="step">
                    <div className="step-number">1</div>
                    <div className="step-content">
                      <h4>Create an API Key</h4>
                      <p>
                        Go to the API Keys tab and click "Create API Key". Give it a descriptive name
                        like "E-commerce Store" or "Mobile App".
                      </p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-number">2</div>
                    <div className="step-content">
                      <h4>Create a Payment Session</h4>
                      <p>
                        When a customer wants to pay, create a payment session from your backend using
                        the API. This returns a secure payment URL.
                      </p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-number">3</div>
                    <div className="step-content">
                      <h4>Redirect Customer</h4>
                      <p>
                        Redirect your customer to the payment URL. They will select their preferred
                        network and token, then complete the payment.
                      </p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-number">4</div>
                    <div className="step-content">
                      <h4>Handle Webhook</h4>
                      <p>
                        Set up a webhook endpoint to receive payment notifications. We'll notify you
                        when payment is completed, failed, or expired.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="doc-card">
            <div
              className="doc-card-header"
              onClick={() => setExpandedSection(expandedSection === 'api' ? null : 'api')}
            >
              <div className="doc-title">
                <Code size={20} />
                <h3>API Reference</h3>
              </div>
              {expandedSection === 'api' ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            </div>
            {expandedSection === 'api' && (
              <div className="doc-card-body">
                <div className="endpoint-section">
                  <h4>Create Payment Session</h4>
                  <div className="endpoint">
                    <span className="method post">POST</span>
                    <code>/api/v1/payments/sessions</code>
                  </div>
                  <p>Creates a new payment session and returns a secure payment URL.</p>

                  <h5>Headers</h5>
                  <table className="params-table">
                    <thead>
                      <tr>
                        <th>Header</th>
                        <th>Required</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><code>X-API-KEY</code></td>
                        <td>Yes</td>
                        <td>Your API key (e.g., pk_live_xxx...)</td>
                      </tr>
                      <tr>
                        <td><code>X-API-SECRET</code></td>
                        <td>Yes</td>
                        <td>
                          Your API secret (sk_...), shown when the key is created and under API Keys. Keep it on your
                          server only. Requests without it will be refused once secret enforcement is switched on.
                        </td>
                      </tr>
                      <tr>
                        <td><code>Content-Type</code></td>
                        <td>Yes</td>
                        <td>application/json</td>
                      </tr>
                    </tbody>
                  </table>

                  <h5>Request Body</h5>
                  <table className="params-table">
                    <thead>
                      <tr>
                        <th>Parameter</th>
                        <th>Type</th>
                        <th>Required</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><code>orderId</code></td>
                        <td>string</td>
                        <td>Yes</td>
                        <td>Your unique order/reference ID</td>
                      </tr>
                      <tr>
                        <td><code>amount</code></td>
                        <td>number</td>
                        <td>Yes</td>
                        <td>Payment amount (e.g., 99.99)</td>
                      </tr>
                      <tr>
                        <td><code>currency</code></td>
                        <td>string</td>
                        <td>No</td>
                        <td>Currency code (default: USD)</td>
                      </tr>
                      <tr>
                        <td><code>description</code></td>
                        <td>string</td>
                        <td>No</td>
                        <td>Payment description shown to customer</td>
                      </tr>
                      <tr>
                        <td><code>successUrl</code></td>
                        <td>string</td>
                        <td>No</td>
                        <td>Redirect URL after successful payment</td>
                      </tr>
                      <tr>
                        <td><code>cancelUrl</code></td>
                        <td>string</td>
                        <td>No</td>
                        <td>Redirect URL if payment is cancelled</td>
                      </tr>
                      <tr>
                        <td><code>callbackUrl</code></td>
                        <td>string</td>
                        <td>No</td>
                        <td>Webhook URL for payment notifications</td>
                      </tr>
                      <tr>
                        <td><code>customerEmail</code></td>
                        <td>string</td>
                        <td>No</td>
                        <td>Customer's email address</td>
                      </tr>
                      <tr>
                        <td><code>expirationMinutes</code></td>
                        <td>number</td>
                        <td>No</td>
                        <td>Session expiration (5-1440 minutes, default: 30)</td>
                      </tr>
                    </tbody>
                  </table>

                  <h5>Response</h5>
                  <div className="code-block">
                    <pre>{`{
  "success": true,
  "message": "Payment session created successfully",
  "data": {
    "sessionId": "ps_abc123...",
    "paymentUrl": "https://pay.maxelpay.com/pay/session/ps_abc123...",
    "orderId": "order_123",
    "amount": 99.99,
    "currency": "USD",
    "status": "pending",
    "expiresAt": "2024-12-15T12:30:00Z",
    "isTestnet": false
  }
}`}</pre>
                  </div>
                </div>

                <div className="endpoint-section">
                  <h4>Get Session Status</h4>
                  <div className="endpoint">
                    <span className="method get">GET</span>
                    <code>/api/v1/payments/sessions/:sessionId/status</code>
                  </div>
                  <p>Check the current status of a payment session.</p>

                  <h5>Response</h5>
                  <div className="code-block">
                    <pre>{`{
  "success": true,
  "data": {
    "sessionId": "ps_abc123...",
    "status": "paid",
    "paidAt": "2024-12-15T12:25:00Z",
    "txHash": "0x123...",
    "paidAmount": 99.99
  }
}`}</pre>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="doc-card">
            <div
              className="doc-card-header"
              onClick={() => setExpandedSection(expandedSection === 'examples' ? null : 'examples')}
            >
              <div className="doc-title">
                <Terminal size={20} />
                <h3>Code Examples</h3>
              </div>
              {expandedSection === 'examples' ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            </div>
            {expandedSection === 'examples' && (
              <div className="doc-card-body">
                <div className="language-tabs">
                  {['curl', 'javascript', 'python', 'php'].map((lang) => (
                    <button
                      key={lang}
                      className={selectedLanguage === lang ? 'active' : ''}
                      onClick={() => setSelectedLanguage(lang)}
                    >
                      {lang.charAt(0).toUpperCase() + lang.slice(1)}
                    </button>
                  ))}
                </div>
                <div className="code-block large">
                  <pre>{getCodeExamples(apiKeys[0]?.apiKey)[selectedLanguage]}</pre>
                  <button
                    className="copy-code-btn"
                    onClick={() => handleCopy('code', getCodeExamples(apiKeys[0]?.apiKey)[selectedLanguage])}
                  >
                    {copiedKey === 'code' ? <Check size={14} /> : <Copy size={14} />}
                    {copiedKey === 'code' ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="doc-card">
            <div
              className="doc-card-header"
              onClick={() => setExpandedSection(expandedSection === 'usecases' ? null : 'usecases')}
            >
              <div className="doc-title">
                <Globe size={20} />
                <h3>Use Cases</h3>
              </div>
              {expandedSection === 'usecases' ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            </div>
            {expandedSection === 'usecases' && (
              <div className="doc-card-body">
                <div className="use-cases-grid">
                  <div className="use-case">
                    <div className="use-case-icon ecommerce">
                      <FileText size={24} />
                    </div>
                    <h4>E-commerce</h4>
                    <p>
                      Accept crypto payments on your online store. Create a payment session when
                      customer clicks "Pay with Crypto" at checkout.
                    </p>
                  </div>
                  <div className="use-case">
                    <div className="use-case-icon saas">
                      <Zap size={24} />
                    </div>
                    <h4>SaaS Subscriptions</h4>
                    <p>
                      Enable crypto payments for subscription plans. Perfect for international
                      customers without access to traditional payment methods.
                    </p>
                  </div>
                  <div className="use-case">
                    <div className="use-case-icon marketplace">
                      <Globe size={24} />
                    </div>
                    <h4>Marketplaces</h4>
                    <p>
                      Let sellers receive crypto payments. Create sessions with seller's details
                      and handle payouts automatically.
                    </p>
                  </div>
                  <div className="use-case">
                    <div className="use-case-icon gaming">
                      <Lock size={24} />
                    </div>
                    <h4>Gaming & Digital Goods</h4>
                    <p>
                      Sell in-game items, digital downloads, or virtual goods with instant
                      crypto payments and automatic delivery.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'webhooks' && (
        <RequireFeature
          feature="webhooks"
          fallback={
            <div className="empty-state">
              <Lock size={48} />
              <h3>Webhooks are a Premium feature</h3>
              <p>Upgrade to configure webhook endpoints and inspect delivery logs.</p>
              <button className="btn-primary" onClick={() => setUpsellFeature('webhooks')}>
                <Lock size={18} />
                Upgrade to unlock
              </button>
            </div>
          }
        >
        <div className="webhooks-section">
          <div className="doc-card">
            <div className="doc-card-header static">
              <div className="doc-title">
                <Terminal size={20} />
                <h3>Webhook Events</h3>
              </div>
            </div>
            <div className="doc-card-body">
              <p className="section-intro">
                Set up a webhook endpoint to receive real-time notifications about payment events.
                Include your webhook URL when creating a payment session using the <code>callbackUrl</code> parameter.
              </p>

              <h4>Available Events</h4>
              <div className="events-list">
                <div className="event-item">
                  <code>payment.completed</code>
                  <span>Payment has been successfully completed and confirmed</span>
                </div>
                <div className="event-item">
                  <code>payment.failed</code>
                  <span>Payment failed or was rejected</span>
                </div>
                <div className="event-item">
                  <code>payment.expired</code>
                  <span>Payment session has expired without payment</span>
                </div>
                <div className="event-item">
                  <code>payment.partial</code>
                  <span>Less than the required amount was paid</span>
                </div>
                <div className="event-item">
                  <code>payment.overpaid</code>
                  <span>More than the required amount was paid (treat as paid)</span>
                </div>
              </div>

              <h4>Webhook Payload</h4>
              <div className="code-block">
                <pre>{`{
  "event": "payment.completed",
  "timestamp": "2024-12-15T12:25:00Z",
  "data": {
    "sessionId": "ps_abc123...",
    "orderId": "order_123",
    "amount": 99.99,
    "currency": "USD",
    "status": "paid",
    "txHash": "0x123...",
    "paidAt": "2024-12-15T12:25:00Z",
    "network": "BSC",
    "token": "USDT"
  }
}`}</pre>
              </div>

              <h4>Example Handler</h4>
              <div className="code-block large">
                <pre>{webhookExample}</pre>
                <button
                  className="copy-code-btn"
                  onClick={() => handleCopy('webhook', webhookExample)}
                >
                  {copiedKey === 'webhook' ? <Check size={14} /> : <Copy size={14} />}
                  {copiedKey === 'webhook' ? 'Copied!' : 'Copy'}
                </button>
              </div>

              <div className="warning-box">
                <Shield size={20} />
                <div>
                  <strong>Security Best Practices</strong>
                  <ul>
                    <li>Verify the X-Webhook-Signature header (HMAC-SHA256 of the raw body with your API secret)</li>
                    <li>Use HTTPS for your webhook endpoint</li>
                    <li>Respond with 200 status quickly, process asynchronously</li>
                    <li>Implement idempotency to handle duplicate events</li>
                    <li>Each webhook is sent once, without automatic retries; resend failed ones from the delivery list on this tab</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* API-03: webhook delivery log */}
          <div className="doc-card">
            <div className="doc-card-header static">
              <div className="doc-title">
                <RefreshCw size={20} />
                <h3>Delivery Log</h3>
              </div>
              <button className="btn-secondary small" onClick={fetchDeliveries} disabled={deliveriesLoading}>
                <RefreshCw size={14} /> Refresh
              </button>
            </div>
            <div className="doc-card-body">
              <p className="section-intro">
                Every webhook attempt and its result. Inspect the payload and resend failed deliveries.
              </p>

              {deliveriesLoading ? (
                <p className="section-intro">Loading deliveries...</p>
              ) : deliveries.length === 0 ? (
                <div className="empty-deliveries">
                  <Globe size={28} />
                  <p>No webhook deliveries yet. They appear here once your payments trigger events.</p>
                </div>
              ) : (
                <div className="delivery-list">
                  {deliveries.map((d) => (
                    <div key={d.id} className="delivery-row">
                      <button
                        className="delivery-summary"
                        onClick={() => setExpandedDelivery(expandedDelivery === d.id ? null : d.id)}
                        aria-label="Toggle delivery details"
                      >
                        {expandedDelivery === d.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        <span className={`delivery-badge ${d.success ? 'ok' : 'fail'}`}>
                          {d.success ? <CheckCircle size={13} /> : <AlertCircle size={13} />}
                          {d.response_status || (d.success ? 'OK' : 'ERR')}
                        </span>
                        <code className="delivery-event">{d.event || 'event'}</code>
                        {d.attempt > 1 && <span className="delivery-attempt">attempt {d.attempt}</span>}
                        <span className="delivery-time">{new Date(d.created_at).toLocaleString('en-US')}</span>
                        <span
                          className="delivery-resend"
                          role="button"
                          tabIndex={0}
                          onClick={(e) => { e.stopPropagation(); handleResendDelivery(d.id); }}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); handleResendDelivery(d.id); } }}
                        >
                          {resendingId === d.id ? 'Resending...' : 'Resend'}
                        </span>
                      </button>
                      {expandedDelivery === d.id && (
                        <div className="delivery-detail">
                          <div className="delivery-meta">
                            <span><strong>URL:</strong> {d.webhook_url}</span>
                            {d.related_type && <span><strong>Source:</strong> {d.related_type} {d.related_id || ''}</span>}
                          </div>
                          <h4>Request payload</h4>
                          <div className="code-block">
                            <pre>{JSON.stringify(d.request_payload, null, 2)}</pre>
                          </div>
                          {d.response_body && (
                            <>
                              <h4>Response</h4>
                              <div className="code-block">
                                <pre>{String(d.response_body).slice(0, 2000)}</pre>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        </RequireFeature>
      )}

      {showCreateModal && (
        <div className="modal-overlay" onClick={handleCloseCreateModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create API Key</h3>
              <button className="close-btn" onClick={handleCloseCreateModal} aria-label="Close dialog">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCreateApiKey}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Environment</label>
                  <div className="key-env-picker" data-env={keyEnv}>
                    <button
                      type="button"
                      className={`key-env-opt ${keyEnv === 'test' ? 'active' : ''}`}
                      onClick={() => setKeyEnv('test')}
                    >
                      <span className="key-env-dot test" />
                      Test key
                      <code>pk_test_…</code>
                    </button>
                    <button
                      type="button"
                      className={`key-env-opt ${keyEnv === 'live' ? 'active' : ''}`}
                      onClick={() => canGoLive && setKeyEnv('live')}
                      disabled={!canGoLive}
                      title={canGoLive ? '' : 'Activate your account to create live keys'}
                    >
                      <span className="key-env-dot live" />
                      Live key
                      <code>pk_live_…</code>
                    </button>
                  </div>
                  <span className="form-hint">
                    {keyEnv === 'test'
                      ? 'Test keys process simulated payments on testnets — no real funds.'
                      : 'Live keys process real cryptocurrency payments.'}
                    {!canGoLive && ' Live keys unlock once your account is activated.'}
                  </span>
                </div>

                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    placeholder="E.g., E-commerce Store, Mobile App"
                    value={newKeyForm.name}
                    onChange={(e) => setNewKeyForm({ ...newKeyForm, name: e.target.value })}
                    required
                  />
                  <span className="form-hint">A descriptive name to identify this API key</span>
                </div>

                <div className="form-group">
                  <label>Description (Optional)</label>
                  <textarea
                    placeholder="Optional description of where this key is used"
                    value={newKeyForm.description}
                    onChange={(e) => setNewKeyForm({ ...newKeyForm, description: e.target.value })}
                    rows={2}
                  />
                </div>

                <div className="form-group">
                  <label>Webhook URL *</label>
                  <input
                    type="url"
                    placeholder="https://yoursite.com/webhook"
                    value={newKeyForm.webhookUrl}
                    onChange={(e) => setNewKeyForm({ ...newKeyForm, webhookUrl: e.target.value })}
                    required
                  />
                  <span className="form-hint">URL to receive payment notifications (required)</span>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={handleCloseCreateModal}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={isCreating}>
                  {isCreating ? (
                    <>
                      <RefreshCw size={16} className="spinning" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus size={16} />
                      Create API Key
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && editingKey && (
        <div className="modal-overlay" onClick={handleCloseEditModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Webhook URL</h3>
              <button className="close-btn" onClick={handleCloseEditModal} aria-label="Close dialog">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleUpdateWebhook}>
              <div className="modal-body">
                <div className="form-group">
                  <label>API Key Name</label>
                  <input
                    type="text"
                    value={editingKey.name}
                    disabled
                    className="disabled-input"
                  />
                </div>

                <div className="form-group">
                  <label>Webhook URL</label>
                  <input
                    type="url"
                    placeholder="https://yoursite.com/webhook"
                    value={editWebhookUrl}
                    onChange={(e) => setEditWebhookUrl(e.target.value)}
                  />
                  <span className="form-hint">URL to receive payment notifications. Leave empty to disable webhook.</span>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={handleCloseEditModal}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={isUpdating}>
                  {isUpdating ? (
                    <>
                      <RefreshCw size={16} className="spinning" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Check size={16} />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showRevokeModal && revokingKey && (
        <div className="modal-overlay" onClick={handleCloseRevokeModal}>
          <div className="modal modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Revoke API Key</h3>
              <button className="close-btn" onClick={handleCloseRevokeModal} aria-label="Close dialog">
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="revoke-warning">
                <div className="warning-icon">
                  <AlertCircle size={48} />
                </div>
                <p className="warning-title">Are you sure you want to revoke this API key?</p>
                <p className="warning-key-name">"{revokingKey.name}"</p>
                <p className="warning-text">
                  This action cannot be undone. Any applications using this API key will no longer be able to authenticate.
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={handleCloseRevokeModal}>
                Cancel
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={handleRevokeKey}
                disabled={isRevoking}
              >
                {isRevoking ? (
                  <>
                    <RefreshCw size={16} className="spinning" />
                    Revoking...
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    Revoke Key
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {rotatingKey && (
        <div className="modal-overlay" onClick={() => !isRotating && setRotatingKey(null)}>
          <div className="modal modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Rotate API Secret</h3>
              <button className="close-btn" onClick={() => setRotatingKey(null)} aria-label="Close dialog" disabled={isRotating}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="revoke-warning">
                <div className="warning-icon">
                  <AlertCircle size={48} />
                </div>
                <p className="warning-title">Issue a new secret for this key?</p>
                <p className="warning-key-name">"{rotatingKey.name}"</p>
                <p className="warning-text">
                  The current secret stops working immediately. Update X-API-SECRET on your server, and verify
                  webhook signatures with the new secret. Your API key and payment links do not change.
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={() => setRotatingKey(null)} disabled={isRotating}>
                Cancel
              </button>
              <button type="button" className="btn-danger" onClick={handleRotateSecret} disabled={isRotating}>
                {isRotating ? (
                  <>
                    <RefreshCw size={16} className="spinning" />
                    Rotating...
                  </>
                ) : (
                  <>
                    <RefreshCw size={16} />
                    Rotate Secret
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {secretNotice && (
        <div className="modal-overlay">
          <div className="modal modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{secretNotice.rotated ? 'New API Secret' : 'Save Your API Secret'}</h3>
            </div>
            <div className="modal-body">
              <p className="warning-text">
                Store this secret on your server only, for example in an environment variable. Send it as the
                X-API-SECRET header with your API key. Never put it in a browser or mobile app.
              </p>
              <div className="key-details">
                {secretNotice.apiKey && (
                  <div className="key-field">
                    <label>API Key</label>
                    <div className="key-value-row">
                      <code>{secretNotice.apiKey}</code>
                      <button
                        className="icon-btn"
                        onClick={() => handleCopy('notice-api', secretNotice.apiKey)}
                        aria-label="Copy API key"
                      >
                        {copiedKey === 'notice-api' ? <Check size={14} /> : <Copy size={14} />}
                      </button>
                    </div>
                  </div>
                )}
                <div className="key-field">
                  <label>API Secret</label>
                  <div className="key-value-row">
                    <code>{secretNotice.secretKey}</code>
                    <button
                      className="icon-btn"
                      onClick={() => handleCopy('notice-secret', secretNotice.secretKey)}
                      aria-label="Copy API secret"
                    >
                      {copiedKey === 'notice-secret' ? <Check size={14} /> : <Copy size={14} />}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-primary" onClick={() => setSecretNotice(null)}>
                I've saved it
              </button>
            </div>
          </div>
        </div>
      )}

      {upsellFeature && (
        <UpsellModal
          feature={upsellFeature}
          onClose={() => setUpsellFeature(null)}
          onUpgrade={() => navigate('/dashboard/billing')}
        />
      )}

      <style>{styles}</style>
    </div>
  );
};

const styles = `
  .developer-page {
    padding: 0;
  }

  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 1.5rem;
    gap: 20px;
    flex-wrap: wrap;
  }

  .page-header h1 {
    font-size: 1.6rem;
    font-weight: 700;
    color: var(--ink);
    letter-spacing: -0.02em;
    margin: 0 0 0.25rem;
  }

  .page-header p {
    color: var(--ink-secondary);
    font-size: 0.875rem;
    margin: 0;
  }

  .header-actions {
    display: flex;
    gap: 0.75rem;
    align-items: center;
  }

  .mode-badge {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 6px 12px;
    border-radius: var(--r-pill);
    font-size: 12px;
    font-weight: 600;
  }

  .mode-badge::before {
    content: "";
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: currentColor;
  }

  .mode-badge.test {
    background: var(--warn-soft);
    color: var(--warn);
  }

  .mode-badge.live {
    background: var(--ok-soft);
    color: var(--ok);
  }

  .admin-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 10px;
    border-radius: var(--r-pill);
    font-size: 12px;
    font-weight: 600;
    background: var(--accent-soft);
    color: var(--accent-ink);
  }

  .merchant-id-badge {
    padding: 0.25rem 0.625rem;
    border-radius: var(--r-pill);
    font-size: 0.7rem;
    font-weight: 600;
    background: var(--accent-soft);
    color: var(--accent-ink);
  }

  /* Stats Row */
  .stats-row {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 1rem;
    margin-bottom: 1.75rem;
  }

  .stat-card {
    position: relative;
    overflow: hidden;
    background: var(--bg-surface);
    border: 1px solid var(--line);
    border-radius: var(--r-lg);
    box-shadow: var(--sh-sm);
    padding: 18px 20px;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    transition: box-shadow 0.18s ease, transform 0.18s ease, border-color 0.18s ease;
  }

  /* Semantic accent bar keyed to the stat's status */
  .stat-card::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: var(--accent);
  }

  .stat-card.active::before { background: var(--ok); }
  .stat-card.inactive::before { background: var(--warn); }
  .stat-card.revoked::before { background: var(--err); }

  .stat-card:hover {
    box-shadow: 0 6px 20px rgba(16, 18, 40, 0.08);
    transform: translateY(-1px);
    border-color: var(--line-strong);
  }

  .stat-icon {
    width: 38px;
    height: 38px;
    border-radius: var(--r-md);
    background: var(--accent-soft);
    display: grid;
    place-items: center;
    color: var(--accent);
    flex-shrink: 0;
  }

  .stat-icon svg {
    color: inherit;
  }

  .stat-icon.active {
    background: var(--ok-soft);
    color: var(--ok);
  }

  .stat-icon.inactive {
    background: var(--warn-soft);
    color: var(--warn);
  }

  .stat-icon.revoked {
    background: var(--err-soft);
    color: var(--err);
  }

  .stat-info {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .stat-value {
    font-family: var(--font-display);
    font-size: 1.9rem;
    font-weight: 700;
    line-height: 1;
    letter-spacing: -0.03em;
    color: var(--ink);
  }

  .stat-label {
    font-size: 0.8rem;
    font-weight: 500;
    color: var(--ink-muted);
  }

  /* Tabs — underline console style */
  .developer-tabs {
    display: flex;
    gap: 2px;
    margin-bottom: 1.75rem;
    border-bottom: 1px solid var(--line);
    padding-bottom: 0;
  }

  .developer-tabs .tab {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 11px 16px;
    border: none;
    background: transparent;
    color: var(--ink-muted);
    font-size: 0.9rem;
    font-weight: 600;
    cursor: pointer;
    border-bottom: 2px solid transparent;
    margin-bottom: -1px;
    transition: color 0.15s;
  }

  .developer-tabs .tab:hover {
    color: var(--ink-secondary);
  }

  .developer-tabs .tab.active {
    color: var(--accent);
    border-bottom-color: var(--accent);
  }

  /* API Keys Header */
  .api-keys-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
  }

  .api-keys-header h3 {
    font-size: 1rem;
    font-weight: 700;
    letter-spacing: -0.01em;
    color: var(--ink);
    margin: 0;
  }

  .create-key-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 10px 16px;
    background: var(--primary);
    color: #fff !important;
    border: 1px solid var(--primary);
    border-radius: var(--r-sm);
    box-shadow: 0 1px 2px rgba(88, 71, 214, 0.35);
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s, box-shadow 0.15s, transform 0.15s;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.25), 0 8px 20px rgba(109, 93, 246, 0.35);
    font-family: var(--iv-sans);
  }

  .create-key-btn:hover {
    background: var(--primary-hover);
    box-shadow: 0 3px 10px rgba(88, 71, 214, 0.28);
  }

  .create-key-btn svg {
    color: #fff !important;
  }

  /* Buttons */
  .btn-primary {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 9px 16px;
    background: var(--primary);
    color: #fff !important;
    border: 1px solid var(--primary);
    border-radius: var(--r-sm);
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.25), 0 8px 20px rgba(109, 93, 246, 0.35);
  }

  .btn-primary svg {
    color: #fff !important;
  }

  .btn-primary:hover:not(:disabled) {
    background: var(--primary-hover);
  }

  .btn-primary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .btn-secondary {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 9px 16px;
    background: var(--bg-surface);
    color: var(--ink);
    border: 1px solid var(--line-strong);
    border-radius: var(--r-sm);
    box-shadow: var(--sh-sm);
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s;
  }

  .btn-secondary:hover {
    background: var(--bg-inset);
  }

  .icon-btn {
    padding: 0.5rem;
    background: transparent;
    border: none;
    color: var(--ink-muted);
    cursor: pointer;
    border-radius: var(--r-sm);
    transition: all 0.15s;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .icon-btn:hover {
    background: var(--bg-inset);
    color: var(--ink);
  }

  .icon-btn.danger:hover {
    background: var(--err-soft);
    color: var(--err);
  }

  /* Loading & Empty States */
  .loading-state,
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 52px 24px;
    background: var(--bg-surface);
    border: 1px solid var(--line);
    border-radius: var(--r-lg);
    text-align: center;
  }

  /* Empty state reads as "nothing here yet" via a dashed edge */
  .empty-state {
    border-style: dashed;
    border-color: var(--line-strong);
  }

  .loading-state svg {
    color: var(--ink-muted);
  }

  /* Leading icon becomes a circular brand badge (padding insets the glyph) */
  .empty-state > svg {
    width: 60px;
    height: 60px;
    padding: 16px;
    box-sizing: border-box;
    border-radius: 50%;
    background: var(--accent-soft);
    color: var(--accent);
    margin-bottom: 1.1rem;
  }

  .loading-state span {
    color: var(--ink-muted);
    font-size: 0.9rem;
    margin-top: 1rem;
  }

  .empty-state p {
    color: var(--ink-muted);
    font-size: 0.9rem;
    max-width: 360px;
    margin: 0 auto;
  }

  .empty-state h3 {
    margin: 0 0 0.4rem;
    font-family: var(--font-display);
    font-size: 1.05rem;
    font-weight: 700;
    color: var(--ink);
  }

  .empty-state .btn-primary {
    margin-top: 1.4rem;
  }

  .empty-hint {
    margin-top: 1rem;
    font-size: 0.82rem;
    color: var(--ink-muted);
  }

  .empty-hint button {
    padding: 0;
    border: none;
    background: none;
    font: inherit;
    color: var(--accent-ink);
    font-weight: 600;
    cursor: pointer;
  }

  .empty-hint button:hover {
    text-decoration: underline;
  }

  .spinning {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  /* API Keys List */
  .api-keys-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .api-key-card {
    background: var(--bg-surface);
    border: 1px solid var(--line);
    border-radius: var(--r-lg);
    box-shadow: var(--sh-sm);
    padding: 1.25rem;
  }

  .api-key-card.revoked {
    opacity: 0.6;
  }

  .key-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 0.75rem;
  }

  .key-title {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .key-title h4 {
    margin: 0;
    font-size: 0.95rem;
    font-weight: 700;
    color: var(--ink);
  }

  .status-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 10px;
    border-radius: var(--r-pill);
    font-size: 12px;
    font-weight: 600;
    text-transform: capitalize;
    background: var(--bg-inset);
    color: var(--ink-secondary);
  }

  .status-badge::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }

  .status-badge.active {
    background: var(--ok-soft);
    color: var(--ok);
  }

  .status-badge.inactive {
    background: var(--warn-soft);
    color: var(--warn);
  }

  .status-badge.revoked {
    background: var(--err-soft);
    color: var(--err);
  }

  .key-actions {
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }

  .key-actions .icon-btn svg {
    color: inherit;
  }

  .toggle-btn {
    padding: 0.375rem 0.875rem;
    border-radius: var(--r-sm);
    font-size: 0.8rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s;
    border: 1px solid var(--line-strong);
    background: var(--bg-surface);
    color: var(--ink-secondary);
  }

  .toggle-btn.active {
    background: var(--ok-soft);
    border-color: var(--ok);
    color: var(--ok);
  }

  .toggle-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .key-description {
    color: var(--ink-secondary);
    font-size: 0.875rem;
    margin: 0 0 1rem;
  }

  .key-details {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .key-field {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
  }

  .key-field label {
    font-size: 11px;
    font-weight: 600;
    color: var(--ink-muted);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .key-value-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: var(--bg-inset);
    padding: 0.5rem 0.75rem;
    border-radius: var(--r-sm);
    border: 1px solid var(--line);
  }

  .key-value-row code {
    flex: 1;
    font-family: var(--font-mono);
    font-size: 0.8rem;
    color: var(--ink);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .key-value-row code.url {
    color: var(--accent);
  }

  .key-meta {
    display: flex;
    gap: 1.5rem;
    flex-wrap: wrap;
    padding-top: 0.75rem;
    border-top: 1px solid var(--line);
    font-size: 0.8rem;
    color: var(--ink-secondary);
  }

  .key-meta strong {
    color: var(--ink);
    font-weight: 600;
  }

  /* Documentation Section */
  .documentation-section {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .doc-card {
    background: var(--bg-surface);
    border: 1px solid var(--line);
    border-radius: var(--r-lg);
    box-shadow: var(--sh-sm);
    overflow: hidden;
  }

  .doc-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 1.25rem;
    cursor: pointer;
    transition: background 0.15s;
  }

  .doc-card-header:hover {
    background: var(--bg-subtle);
  }

  .doc-card-header.static {
    cursor: default;
  }

  .doc-card-header.static:hover {
    background: transparent;
  }

  .doc-title {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .doc-title svg {
    color: var(--accent);
  }

  .doc-title h3 {
    margin: 0;
    font-size: 0.95rem;
    font-weight: 700;
    color: var(--ink);
  }

  .doc-card-header > svg {
    color: var(--ink-muted);
  }

  .doc-card-body {
    padding: 1.25rem;
    border-top: 1px solid var(--line);
  }

  /* Steps */
  .steps {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .step {
    display: flex;
    gap: 1rem;
  }

  .step-number {
    width: 32px;
    height: 32px;
    background: var(--accent-soft);
    color: var(--accent-ink);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 0.85rem;
    flex-shrink: 0;
  }

  .step-content h4 {
    margin: 0 0 0.5rem;
    font-size: 0.95rem;
    font-weight: 700;
    color: var(--ink);
  }

  .step-content p {
    margin: 0;
    color: var(--ink-secondary);
    font-size: 0.875rem;
    line-height: 1.5;
  }

  /* Code Blocks */
  .code-block {
    background: #1a1a1e !important;
    border-radius: var(--r-md);
    padding: 16px 18px;
    overflow-x: auto;
    position: relative;
    border: 1px solid #27272a;
  }

  .code-block pre {
    margin: 0;
    color: #e4e4e7 !important;
    font-family: var(--font-mono);
    font-size: 12.5px;
    line-height: 1.6;
    white-space: pre-wrap;
    word-break: break-word;
    background: transparent !important;
  }

  .code-block.large {
    max-height: 400px;
    overflow-y: auto;
  }

  .copy-code-btn {
    position: absolute;
    top: 10px;
    right: 10px;
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 4px 9px;
    background: #27272a;
    border: 1px solid #3f3f46;
    border-radius: 6px;
    color: #a1a1aa !important;
    font-size: 11px;
    cursor: pointer;
    transition: all 0.15s;
  }

  .copy-code-btn svg {
    color: #a1a1aa !important;
  }

  .copy-code-btn:hover {
    background: #3f3f46;
    color: #e4e4e7 !important;
  }

  /* Language Tabs */
  .language-tabs {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }

  .language-tabs button {
    padding: 0.5rem 1rem;
    background: var(--bg-surface);
    border: 1px solid var(--line-strong);
    border-radius: var(--r-sm);
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--ink-secondary);
    cursor: pointer;
    transition: all 0.15s;
  }

  .language-tabs button:hover {
    color: var(--ink);
    background: var(--bg-inset);
  }

  .language-tabs button.active {
    background: var(--primary);
    border-color: var(--primary);
    color: #fff;
  }

  /* Endpoint */
  .endpoint-section {
    margin-bottom: 2rem;
  }

  .endpoint-section:last-child {
    margin-bottom: 0;
  }

  .endpoint-section h4 {
    margin: 0 0 0.75rem;
    font-size: 1rem;
    font-weight: 700;
    color: var(--ink);
  }

  .endpoint-section h5 {
    margin: 1.25rem 0 0.75rem;
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--ink-secondary);
  }

  .endpoint-section > p {
    color: var(--ink-secondary);
    margin: 0 0 1rem;
  }

  .endpoint {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.75rem;
  }

  .endpoint .method {
    padding: 0.25rem 0.625rem;
    border-radius: var(--r-sm);
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
  }

  .endpoint .method.post {
    background: var(--info-soft);
    color: var(--info);
  }

  .endpoint .method.get {
    background: var(--ok-soft);
    color: var(--ok);
  }

  .endpoint code {
    font-family: var(--font-mono);
    font-size: 0.875rem;
    color: var(--ink);
  }

  /* Params Table */
  .params-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.85rem;
  }

  .params-table th,
  .params-table td {
    padding: 0.75rem;
    text-align: left;
    border-bottom: 1px solid var(--line);
  }

  .params-table th {
    font-weight: 600;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--ink-muted);
    background: var(--bg-subtle);
  }

  .params-table td {
    color: var(--ink-secondary);
  }

  .params-table code {
    background: var(--accent-soft);
    padding: 0.125rem 0.375rem;
    border-radius: var(--r-sm);
    font-size: 0.8rem;
    color: var(--accent-ink);
    font-family: var(--font-mono);
    font-weight: 600;
  }

  /* Use Cases */
  .use-cases-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }

  .use-case {
    background: var(--bg-subtle);
    border: 1px solid var(--line);
    border-radius: var(--r-md);
    padding: 1.25rem;
  }

  .use-case-icon {
    width: 40px;
    height: 40px;
    border-radius: var(--r-md);
    display: grid;
    place-items: center;
    margin-bottom: 1rem;
  }

  .use-case-icon.ecommerce {
    background: var(--info-soft);
    color: var(--info);
  }

  .use-case-icon.saas {
    background: var(--accent-soft);
    color: var(--accent);
  }

  .use-case-icon.marketplace {
    background: var(--ok-soft);
    color: var(--ok);
  }

  .use-case-icon.gaming {
    background: var(--accent-soft);
    color: var(--accent-ink);
  }

  .use-case h4 {
    margin: 0 0 0.5rem;
    font-size: 0.95rem;
    font-weight: 700;
    color: var(--ink);
  }

  .use-case p {
    margin: 0;
    color: var(--ink-secondary);
    font-size: 0.85rem;
    line-height: 1.5;
  }

  /* Webhooks */
  .section-intro {
    color: var(--ink-secondary);
    margin: 0 0 1.5rem;
    line-height: 1.5;
  }

  .section-intro code {
    background: var(--accent-soft);
    padding: 0.25rem 0.5rem;
    border-radius: var(--r-sm);
    color: var(--accent-ink);
    font-family: var(--font-mono);
    font-weight: 600;
  }

  .doc-card-body h4 {
    color: var(--ink);
    font-size: 0.95rem;
    font-weight: 700;
    margin: 1.5rem 0 0.75rem;
  }

  .doc-card-body h4:first-child {
    margin-top: 0;
  }

  .events-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }

  .btn-secondary.small {
    padding: 0.4rem 0.7rem;
    font-size: 0.8rem;
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
  }

  .empty-deliveries {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    padding: 2rem 1rem;
    color: var(--ink-muted);
    text-align: center;
  }

  .delivery-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .delivery-row {
    border: 1px solid var(--line);
    border-radius: var(--r-sm);
    overflow: hidden;
  }

  .delivery-summary {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.6rem 0.75rem;
    background: transparent;
    border: none;
    cursor: pointer;
    text-align: left;
    color: var(--ink);
  }

  .delivery-summary:hover { background: var(--bg-subtle); }

  .delivery-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.75rem;
    font-weight: 600;
    padding: 0.15rem 0.45rem;
    border-radius: var(--r-sm);
  }

  .delivery-badge.ok { background: var(--ok-soft); color: var(--ok); }
  .delivery-badge.fail { background: var(--err-soft); color: var(--err); }

  .delivery-event { font-size: 0.8rem; font-family: var(--font-mono); }
  .delivery-attempt { font-size: 0.72rem; color: var(--ink-muted); }
  .delivery-time { font-size: 0.75rem; color: var(--ink-muted); margin-left: auto; }

  .delivery-resend {
    font-size: 0.78rem;
    font-weight: 600;
    color: var(--accent);
    padding: 0.2rem 0.4rem;
  }
  .delivery-resend:hover { text-decoration: underline; }

  .delivery-detail {
    padding: 0.5rem 0.85rem 0.85rem;
    border-top: 1px solid var(--line);
  }

  .delivery-meta {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    font-size: 0.8rem;
    color: var(--ink-secondary);
    margin: 0.5rem 0;
    word-break: break-all;
  }

  .event-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.875rem 1rem;
    background: var(--bg-subtle);
    border: 1px solid var(--line);
    border-radius: var(--r-sm);
  }

  .event-item code {
    font-family: var(--font-mono);
    font-size: 0.85rem;
    color: var(--accent-ink);
    font-weight: 600;
    min-width: 180px;
  }

  .event-item span {
    color: var(--ink-secondary);
    font-size: 0.85rem;
    text-align: right;
  }

  .warning-box {
    display: flex;
    gap: 1rem;
    padding: 1rem 1.25rem;
    background: var(--warn-soft);
    border: 1px solid var(--warn);
    border-radius: var(--r-md);
    margin-top: 1.5rem;
  }

  .warning-box svg {
    color: var(--warn);
    flex-shrink: 0;
    margin-top: 0.125rem;
  }

  .warning-box strong {
    color: var(--ink);
    display: block;
    margin-bottom: 0.5rem;
  }

  .warning-box ul {
    margin: 0;
    padding-left: 1.25rem;
    color: var(--ink-secondary);
    font-size: 0.85rem;
    line-height: 1.6;
  }

  /* Modal */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 1rem;
  }

  .modal {
    background: var(--bg-surface);
    border: 1px solid var(--line);
    border-radius: var(--r-lg);
    width: 100%;
    max-width: 500px;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: var(--sh-pop);
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.25rem 1.5rem;
    border-bottom: 1px solid var(--line);
  }

  .modal-header h3 {
    margin: 0;
    font-size: 1rem;
    font-weight: 700;
    color: var(--ink);
  }

  .close-btn {
    padding: 0.375rem;
    background: transparent;
    border: none;
    color: var(--ink-muted);
    cursor: pointer;
    border-radius: var(--r-sm);
    transition: all 0.15s;
  }

  .close-btn:hover {
    background: var(--bg-inset);
    color: var(--ink);
  }

  .modal-body {
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
  }

  .modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    padding: 1.25rem 1.5rem;
    border-top: 1px solid var(--line);
  }

  /* Form */
  .form-group {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
  }

  .form-group label {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--ink);
  }

  .form-group input,
  .form-group textarea,
  .form-group select {
    padding: 11px 13px;
    border: 1px solid var(--line-strong);
    border-radius: var(--r-sm);
    font-size: 0.875rem;
    background: var(--bg-surface);
    color: var(--ink);
    transition: border-color 0.13s, box-shadow 0.13s;
  }

  .form-group input::placeholder,
  .form-group textarea::placeholder {
    color: var(--ink-muted);
  }

  .form-group input:focus,
  .form-group textarea:focus,
  .form-group select:focus {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 3px var(--accent-soft);
  }

  .form-group textarea {
    resize: vertical;
    min-height: 60px;
  }

  .form-hint {
    font-size: 0.75rem;
    color: var(--ink-muted);
  }

  .form-row {
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: 1rem;
  }

  /* Responsive */
  @media (max-width: 768px) {
    .stats-row {
      grid-template-columns: repeat(2, 1fr);
    }

    .use-cases-grid {
      grid-template-columns: 1fr;
    }

    .form-row {
      grid-template-columns: 1fr;
    }

    .key-meta {
      flex-direction: column;
      gap: 0.5rem;
    }

    .params-table {
      display: block;
      overflow-x: auto;
    }
  }

  .disabled-input {
    background: var(--bg-inset) !important;
    color: var(--ink-muted) !important;
    cursor: not-allowed !important;
  }

  /* Revoke Confirmation Modal */
  .modal-sm {
    max-width: 420px;
  }

  .revoke-warning {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 0.5rem 0;
  }

  .revoke-warning .warning-icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: var(--err-soft);
    display: grid;
    place-items: center;
    margin-bottom: 1.25rem;
  }

  .revoke-warning .warning-icon svg {
    color: var(--err);
  }

  .revoke-warning .warning-title {
    font-size: 1rem;
    font-weight: 700;
    color: var(--ink);
    margin: 0 0 0.5rem;
  }

  .revoke-warning .warning-key-name {
    font-size: 1.05rem;
    font-weight: 700;
    color: var(--err);
    margin: 0 0 1rem;
  }

  .revoke-warning .warning-text {
    font-size: 0.875rem;
    color: var(--ink-secondary);
    margin: 0;
    line-height: 1.5;
  }

  .btn-danger {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 9px 16px;
    background: var(--err);
    color: #fff !important;
    border: 1px solid var(--err);
    border-radius: var(--r-sm);
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: filter 0.15s;
  }

  .btn-danger svg {
    color: #fff !important;
  }

  .btn-danger:hover:not(:disabled) {
    filter: brightness(0.92);
  }

  .btn-danger:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export default Developer;
